/**
 * The room page's activation gate, as the app sees it (shared/prerender-gate.js,
 * the first script on index.html).
 *
 * A portal may prerender the room page while a call is likely. The bundle is
 * evaluated then, before anyone sees the page, so its module-level work with
 * an effect outside the page (error reporting, the boot telemetry, the join
 * marks) waits for the page to be shown. On every page that was not
 * prerendered, the callbacks run at once, as they always did.
 */
const HANDOFF_OUTCOMES = ['none', 'hit', 'missing', 'invalid'];
function gateWindow() {
    return typeof window === 'undefined' ? undefined : window;
}
/**
 * Runs fn once the room page is shown: now on every page that was not
 * prerendered (and on any page without the gate), in registration order
 * after the handoff on one that was.
 *
 * @param {Function} fn - The work.
 * @returns {void}
 */
export function whenRoomActivated(fn) {
    const run = gateWindow()?.__vrsWhenActivated;
    if (typeof run === 'function') {
        run(fn);
        return;
    }
    fn();
}
/**
 * How this page was entered. Meaningful once it is shown (inside a
 * whenRoomActivated callback or later).
 *
 * @returns {RoomEntry}
 */
export function getRoomEntry() {
    const gate = gateWindow()?.__vrsActivation;
    const activationStart = typeof gate?.activationStart === 'number' && Number.isFinite(gate.activationStart)
        && gate.activationStart > 0 ? gate.activationStart : 0;
    const handoff = HANDOFF_OUTCOMES.includes(gate?.handoff)
        ? gate?.handoff : 'none';
    return {
        activationStart,
        entryMode: gate?.entryMode === 'prerender' && activationStart > 0 ? 'prerender' : 'cold',
        gateViolations: typeof gate?.violationCount === 'number' ? gate.violationCount : 0,
        handoff,
        handoffReason: handoff === 'invalid' && typeof gate?.handoffReason === 'string' ? gate.handoffReason : undefined
    };
}
/**
 * ms on the page's performance clock when it was shown: 0 unless it was
 * prerendered. Every join timing is measured from here, because on a
 * prerendered page performance.timeOrigin is when the prerender started.
 *
 * @returns {number}
 */
export function getActivationStartMs() {
    return getRoomEntry().activationStart;
}
/**
 * How far a prerendered page had loaded when it was shown, from the
 * document's own timings: still receiving its HTML, still loading or running
 * its scripts, or ready (DOMContentLoaded done).
 *
 * @param {number} activationStart - When the page was shown (0: never prerendered).
 * @param {Object} [navigation] - PerformanceNavigationTiming.
 * @returns {PreActivationStage|undefined}
 */
export function describePreActivationStage(activationStart, navigation) {
    if (!(activationStart > 0)) {
        return undefined;
    }
    const responseEnd = navigation?.responseEnd;
    const domReady = navigation?.domContentLoadedEventEnd;
    if (typeof responseEnd !== 'number' || responseEnd <= 0 || responseEnd > activationStart) {
        return 'html';
    }
    if (typeof domReady !== 'number' || domReady <= 0 || domReady > activationStart) {
        return 'bundle';
    }
    return 'dom';
}
